/* eslint-disable @typescript-eslint/no-unsafe-argument */
import {
  addMinutes,
  subMinutes,
  parse
} from 'date-fns'
import minBy from 'lodash/minBy'
import maxBy from 'lodash/maxBy'
import {
  API_URL,
  ROLES
} from '../constant'
import {
  UPDATE_LIST,
  APPEND_LIST,
  UPDATE_SELECTED,
  UPDATE_PDF_LIST,
  CLEAR_PDF_LIST,
  PDF_READY,
  UPDATE_RAF,
  UPDATE_PAGES_SEEN
} from '../reducer/encounter'
import { api } from './api'
import { searchGenerator } from './search'
import {
  clearSelected as clearSelectedPatient,
  updateEncounterConditions
} from './patient'
import { auditDailyHuddleRead } from './audit'
import { EncounterConditionDisplay } from '../component/authenticated/types'
import { UPDATE_PREVIOUS_CONDITIONS } from '../reducer/condition'
import { EncounterNoteBody } from '../../types'

// changed to string based upon CONDITION_TYPE object in component/encounter/detail.js
export const updatePreviousConditions = (
  previous: EncounterConditionDisplay[]
) => ({
  type: UPDATE_PREVIOUS_CONDITIONS,
  payload: previous
})

export const clearSelected = () => ({
  type: UPDATE_SELECTED,
  payload: {}
})

export const updateList = (
  list: any[],
  onSchedule: boolean,
  physicianDay: any | undefined
) => ({
  type: UPDATE_LIST,
  payload: { list, onSchedule, physicianDay }
})

export const appendList = (
  list: any
) => ({
  type: APPEND_LIST,
  payload: list
})

export const updateSelected = (
  body: any
) => ({
  type: UPDATE_SELECTED,
  payload: body
})

export const updatePdfList = (
  body: any
) => ({
  type: UPDATE_PDF_LIST,
  payload: body
})

export const clearPdfList = () => ({
  type: CLEAR_PDF_LIST
})

export const setPdfReady = (
  isReady: boolean
) => ({
  type: PDF_READY,
  payload: isReady
})

export const updateRafValues = (
  raf: any
) => ({
  type: UPDATE_RAF,
  payload: raf
})

export const loadSelected = (
  id: string
): (dispatch: any) => Promise<void> => dispatch => api
  .get(API_URL.GET_ENCOUNTER_DETAIL_BY_ID + id).then(({ data }) => {
    dispatch(updatePreviousConditions(data.previous as EncounterConditionDisplay[]))
    dispatch(updateEncounterConditions(data.conditions as EncounterConditionDisplay[]))
    dispatch(updateSelected(data))
  })

export const clearAndLoadSelected = (
  id: string
) => (dispatch: any) => {
  dispatch(clearSelected())
  dispatch(clearSelectedPatient())
  dispatch(loadSelected(id))
}

export const loadPdfList = (list: string[]) => {
  if (list && list.length > 0) {
    const encounterId = list.shift()
    return (dispatch: any) => api
      .get(`${API_URL.GET_ENCOUNTER_DETAIL_BY_ID}${encounterId}`)
      .then(({ data }) => {
        dispatch(updatePdfList(data))
        dispatch(loadPdfList(list))
      })
  }
  return (dispatch: any) => {
    auditDailyHuddleRead()
    dispatch(setPdfReady(true))
  }

}

type LoadEncounterProps = {
  userRole: keyof typeof ROLES
  query: Record<string, string> | null
  limit: number | undefined
  offset: number | undefined
  sort: string | null
  append: unknown | undefined
  checkOnSchedule?: any
}

export const loadEncounters = (props: LoadEncounterProps) => {
  const { userRole } = props
  const ON_SCHEDULE_BUFFER = 30
  const onSchedule = (encounters: any) => {
    if (!encounters || encounters.length === 0) return false
    // Identifies the highest/lowest encounter, pulls the start date and applies buffer.
    const lowest = subMinutes(
      parse(
        minBy(
          encounters,
          (e: any) => parse(e.start)
        ).start
      ),
      ON_SCHEDULE_BUFFER
    )
    const highest = addMinutes(
      parse(
        maxBy(
          encounters,
          (e: any) => parse(e.start)
        ).start
      ),
      ON_SCHEDULE_BUFFER
    )
    return new Date() < highest && new Date() > lowest
  }
  return async (dispatch: any) => {
    try {
      const params = searchGenerator(
        props.query,
        props.limit,
        props.offset,
        props.sort,
        undefined
      )
      const start = props.query && props.query.start
        ? props.query.start
        : undefined
      const { data } = await api.get(API_URL.ENCOUNTER_SEARCH_WITH_ROLE(userRole), { params })
      if (!props.append) {
        dispatch(
          updateList(
            data,
            props.checkOnSchedule
              ? onSchedule(data)
              : false,
            start
          )
        )
      } else {
        dispatch(
          appendList(
            data
            // props.checkOnSchedule
            //   ? onSchedule(data)
            //   : false,
            // start
          )
        )
      }
    } catch {
      // 403 errors are already handled by api.defaultErrorHandler
    }
  }
}

export const updateRaf = (
  patientId: string,
  encounterId: string
) => (dispatch: any) => api
  .put(`${API_URL.RAF_UPDATE}/patient/${patientId}/encounter/${encounterId}`)
  .then(({ data }) => {
    dispatch(updateRafValues(data))
  })

export const setEncounterStatus = (
  encounterId: string,
  status: string,
  role: string
) => (dispatch: any) => {
  const url = `${API_URL.UPDATE_ENCOUNTER_STATUS_WITH_ROLE(role)}/${encounterId}/${status}`
  return api.put(url, {}).then(() => {
    dispatch(loadSelected(encounterId))
  })
}

export const addEncounterNote = (
  encounterId: string,
  note: EncounterNoteBody
) => async (dispatch: any) => {
  const url = API_URL.CREATE_ENCOUNTER_NOTE
  await api.post(url, note)
  dispatch(loadSelected(encounterId))
}

// action creator
export const updatePagesSeen = (pagesSeen: any) => ({
  type: UPDATE_PAGES_SEEN,
  payload: pagesSeen
})
